import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Button, Spin, Modal, message, Form, Input } from 'antd';
import { AppDispatch, RootState } from '../../redux/store';
import { getAllPackagesByResellerId, sellPackage } from '../../redux/slices/packageSlice';
import { SellPackageRequest } from '../../models/SellPackageRequest';
import api from '../../api/axios';

export function PosPackageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { packages, loading } = useSelector((state: RootState) => state.packages);

  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [sellModalVisible, setSellModalVisible] = useState(false);
  const [upayModalVisible, setUpayModalVisible] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [upayForm] = Form.useForm();

  useEffect(() => {
    if (!packages.length) {
      dispatch(getAllPackagesByResellerId());
    }
  }, [dispatch, packages.length]);

  useEffect(() => {
    if (packages.length && id) {
      const found = packages.find((pkg) => pkg.id === Number(id));
      setSelectedPackage(found || null);
    }
  }, [packages, id]);

  const handleBack = () => {
    navigate('/plans');
  };

  const handleSell = () => {
    setSellModalVisible(true);
  };

  const handleUpaySell = () => {
    setUpayModalVisible(true);
  };

  const confirmSell = async () => {
    if (!selectedPackage) return;

    setProcessing(true);
    try {
      const payload: SellPackageRequest = {
        packageId: selectedPackage.prepaidPackageTemplateId,
        email: null,
        phoneNumber: null,
        iccid: selectedPackage.iccid,
        cost: selectedPackage.cost
      };

      const resultAction = await dispatch(sellPackage(payload));

      if (sellPackage.fulfilled.match(resultAction)) {
        const { urlQrCode, subscriberId } = resultAction.payload;

        message.success('Package sold successfully!');
        setSellModalVisible(false);

        navigate('/sale/qr', {
          state: {
            esimUrl: urlQrCode,
            subscriberId
          }
        });
      } else {
        message.error('Failed to sell package.');
      }
    } catch (error) {
      message.error('Failed to sell package.');
    } finally {
      setProcessing(false);
    }
  };

  const confirmUpaySell = async () => {
    if (!selectedPackage) return;

    try {
      const values = await upayForm.validateFields();
      setProcessing(true);
      const authToken = localStorage.getItem('token') || '';

      const response = await fetch('/api/upay-orders/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          packageId: Number(selectedPackage.id ?? 0),
          packageTemplateId: Number(selectedPackage.prepaidPackageTemplateId ?? 0),
          customerName: values.customerName,
          customerEmail: values.customerEmail,
          customerPhone: values.customerPhone,
          amount: Number(selectedPackage.cost ?? 0)
        })
      });

      const data = await response.json();

      if (!response.ok || !data?.success || !data?.localOrderId) {
        throw new Error('Failed to create UPay order');
      }

      const cashOrderResponse = await api.post('/upay/create-cash-order', {
        orderId: data.localOrderId
      });

      const cashOrderData = cashOrderResponse.data;

      if (!cashOrderData?.success) {
        throw new Error(cashOrderData?.resultMessage || 'Failed to send UPay invoice email');
      }

      setUpayModalVisible(false);
      upayForm.resetFields();

      message.success('UPay invoice sent successfully to customer email.');
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Failed to start UPay payment.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !selectedPackage) {
    return (
      <div style={{ textAlign: 'center', marginTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  const dataMb = Number(selectedPackage?.dataByte)
    ? (selectedPackage.dataByte / (1024 * 1024)).toFixed(0)
    : '-';

  return (
    <div style={{ padding: 24 }}>
      <Button onClick={handleBack} style={{ marginBottom: 24 }}>
        Back
      </Button>

      <Card
        style={{ maxWidth: 500, margin: '0 auto', textAlign: 'center', padding: 24 }}
        bodyStyle={{ padding: 24 }}
      >
        <h2>{selectedPackage.prepaidPackageTemplateName}</h2>
        <p style={{ fontSize: 18, marginBottom: 12 }}>
          <strong>{dataMb} MB</strong>
        </p>
        <p style={{ marginBottom: 8 }}>{selectedPackage.periodDays} Days</p>
        <p style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff', marginBottom: 24 }}>
          {selectedPackage.cost}€
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button type="primary" size="large" onClick={handleSell}>
            Sell Package
          </Button>
          <Button size="large" onClick={handleUpaySell}>
            Pay with UPay
          </Button>
        </div>
      </Card>

      <Modal
        title="Sell Package"
        open={sellModalVisible}
        onCancel={() => setSellModalVisible(false)}
        onOk={confirmSell}
        okText="Confirm Sale"
        confirmLoading={processing}
      >
        <div className="sell-confirmation">
          <p className="sell-confirmation__intro">
            Review the sale details. A QR code will be generated after you confirm.
          </p>
          <div className="sell-confirmation__summary">
            <div className="sell-confirmation__row">
              <span className="sell-confirmation__label">Plan</span>
              <span className="sell-confirmation__value">{selectedPackage.prepaidPackageTemplateName}</span>
            </div>
            <div className="sell-confirmation__row">
              <span className="sell-confirmation__label">Data</span>
              <span className="sell-confirmation__value">{dataMb} MB</span>
            </div>
            <div className="sell-confirmation__row">
              <span className="sell-confirmation__label">Validity</span>
              <span className="sell-confirmation__value">{selectedPackage.periodDays} Days</span>
            </div>
            <div className="sell-confirmation__row">
              <span className="sell-confirmation__label">Price</span>
              <span className="sell-confirmation__value">{selectedPackage.cost}€</span>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="Pay with UPay"
        open={upayModalVisible}
        onCancel={() => {
          setUpayModalVisible(false);
          upayForm.resetFields();
        }}
        onOk={confirmUpaySell}
        okText="Generate Payment Invoice"
        confirmLoading={processing}
      >
        <Form form={upayForm} layout="vertical">
          <Form.Item
            label="Full Name"
            name="customerName"
            rules={[{ required: true, message: 'Please enter the customer name' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="customerEmail"
            rules={[
              { required: true, message: 'Please enter the customer email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="john@example.com" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="customerPhone"
            rules={[{ required: true, message: 'Please enter the customer phone number' }]}
          >
            <Input placeholder="383690000000" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PosPackageDetail;
